<template>
  <div class="api-user-configuration">
    <Loading
      class="vasion-loading-indicator"
      :active.sync="isLoading"
      :is-full-page="false"
      :color="loaderColor"
      loader="dots"
      :background-color="loaderBackgroundColor"
    />
    <div class="configuration-form-container">
      <h1 class="configuration-form-title">{{ mode }} API User</h1>
      <VasionInput
        v-model="apiUser.userName"
        title="Username"
        class="input-style"
        inputType="top-white"
        required
      />
      <VasionInput
        v-model="apiUser.email"
        title="Email"
        class="input-style"
        inputType="top-white"
        required
      />
      <VasionCheckbox
        id="active-user"
        class="checkbox"
        name="active-user"
        :checked="apiUser.active"
        @change="apiUser.active = !apiUser.active"
      >
        Active
      </VasionCheckbox>
      <div class="button-group-01">
        <VasionButton
          classProp="primary-light"
          @vasionButtonClicked="cancelClick()"
        >
          CANCEL
        </VasionButton>
        <VasionButton
          classProp="primary"
          @vasionButtonClicked="saveUserClick()"
        >
          SAVE
        </VasionButton>
      </div>
    </div>
    <VasionSnackbar
      id="snackbar-config-api-user"
      ref="snackbarConfigAPIUser"
      :showSnackbarBool.sync="showSnackbar"
      :snackbarImage="snackbarImage"
      :snackbarSubTitle="snackbarSubTitle"
      :snackbarTitle="snackbarTitle"
    />
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay'
import { loaderBackgroundColor, loaderColor } from '@/assets/js/styleConfig'

export default {
  name: 'APIUserConfiguration',
  components: {
    Loading,
  },
  props: {
    apiUserId: {
      type: Number,
      default: 0,
      required: false,
    },
  },
  data: function () {
    return {
      apiUser: {
        userID: 0,
        userName: '',
        password: null,
        firstName: 'API',
        middleName: '',
        lastName: 'User',
        email: '',
        department: null,
        mobilePhone: '',
        loginType: 7,
        active: true,
        admin: false,
      },
      isLoading: false,
      loaderBackgroundColor: loaderBackgroundColor,
      loaderColor: loaderColor,
      mode: 'create',
      state: '',
      showSnackbar: false,
      snackbarImage: false,
      snackbarSubTitle: '',
      snackbarTitle: '',
      showConfigureApiUserDialogTest: false,
    }
  },
  async created() {
    if (this.apiUserId <= 0) return
    this.mode = 'edit'
    await this.loadApiUserDataForEditing()
  },
  methods: {
    cancelClick() {
      this.$emit('cancel')
    },
    async loadApiUserDataForEditing() {
      this.isLoading = true

      const apiUser = await this.$store.dispatch(
        'security/getUserDetails',
        this.apiUserId
      )

      this.apiUser.userID = apiUser.userID
      this.apiUser.userName = apiUser.userName
      this.apiUser.email = apiUser.email
      this.apiUser.active = apiUser.active

      this.isLoading = false
    },
    async saveUserClick() {
      this.isLoading = true

      const isConfigValid = await this.validateApiUserConfig()
      if (!isConfigValid) {
        this.isLoading = false
        return
      }

      const saveResult = await this.$store.dispatch(
        'security/saveUserDetails',
        this.apiUser
      )

      this.isLoading = false
      if (saveResult?.ResultMessage) {
        this.snackbarTitle = 'Error Saving API User Configuration'
        this.snackbarSubTitle = saveResult.ResultMessage
        this.snackbarImage = false
        this.showSnackbar = true
      } else if (!saveResult || !saveResult.userID) {
        this.snackbarTitle = 'Error Saving API User Configuration'
        this.snackbarSubTitle = 'Unable to save API User. Check logs.'
        this.snackbarImage = false
        this.showSnackbar = true
      } else {
        this.$emit(
          'showSnackbarWithMessage',
          'API User Configuration Saved Successfully',
          '',
          false
        )
        await new Promise(resolve => setTimeout(resolve, 2000))

        if (this.mode == 'edit') this.$emit('close-and-refresh')
        else this.$emit('open-tokens-list', saveResult)
      }
    },
    async validateApiUserConfig() {
      const validationRules = [
        {
          condition: this.apiUser.userName === '',
          message: 'Username is required',
        },
        {
          condition: this.apiUser.userName.includes(' '),
          message: 'Username cannot contain blank spaces',
        },
        {
          condition: this.apiUser.email === '',
          message: 'Email is required',
        },
        {
          condition: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.apiUser.email),
          message: 'Email is not valid',
        },
      ]

      for (const rule of validationRules) {
        if (rule.condition) {
          this.showSnackbarWithMessage(rule.message)
          return false
        }
      }

      this.$refs.snackbarConfigAPIUser.closeSnackbar()
      return true
    },
    showSnackbarWithMessage(message) {
      this.snackbarTitle = 'API User Configuration'
      this.snackbarSubTitle = message
      this.snackbarImage = false
      this.showSnackbar = true
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';
@import '@/assets/css/light-theme-system-palette.scss';

.api-user-configuration {
  position: relative;
}

::v-deep .vasion-loading-indicator {
  height: unset;
}

.configuration-form-container {
  min-width: 390px;
  width: 581px;
  max-width: 581px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex-shrink: 0;
}

.configuration-form-container > * {
  width: 100%;
}

.vasion-droplist {
  width: 100% !important;
}

.configuration-form-title {
  text-align: center;
  text-transform: capitalize;
  color: $on-surface-variant;
  font-family: $font-family-book;
  font-size: 32px;
  font-style: normal;
  font-weight: 400;
  line-height: 40px;
  margin: 0px;
}

.two-columns {
  display: flex;
  align-items: center;
  align-content: center;
  gap: 8px;
  align-self: stretch;
  flex-wrap: wrap;
}

.two-columns > * {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  flex: 1 0 0;
}

.checkbox {
  vertical-align: middle;
  height: 48px;
  display: flex;
  justify-content: left;
  align-items: center;
}
</style>
